import type {
  PriceFacts,
  WireBooking,
  WireContractCalculations,
  WireContractDiscount,
  WireListing,
  WirePayAtVenue,
} from "../types";

import { createEmptyPriceFacts } from "./createEmptyPriceFacts";
import { getFoodDrinkType } from "./foodDrinkType.utils";

type GuestPaymentSettlement = {
  currentPaidTotal: number;
  dueNow: number;
  isOfferFromInquiry?: boolean;
  refund: number | null;
  scheduledChargeDate: string | null;
  scheduledPayment: number | null;
};

type MapCalculationsToPriceFactsParams = {
  calculations: WireContractCalculations | null | undefined;
  listing: WireListing | null | undefined;
  spaceRentalBooking?: WireBooking | null;
  durationHours?: number | null;
  eventEnd?: string | null;
  /**
   * Booking / event start (ISO). Defaults to `spaceRentalBooking.dts_start`
   * when omitted.
   */
  bookingStart?: string | null;
  upcomingChargeDate?: string | null;
  credits?: number;
  /** When true, leave `taxes` null (not calculated yet). */
  disableSalesTax?: boolean;
  /**
   * Whether the master contract is actually on a payment schedule. Quotes and
   * contracts carry `payment_schedule` / `split_payment` blocks (fee, parts and
   * `split_payment_charge_date`) even when the booking pays in full, so the
   * schedule fee, `dueToday` and `upcomingCharge` are only real when this is
   * true.
   */
  hasPaymentSchedule?: boolean;
  /**
   * Whether checkout has already captured money. Until it has, the schedule's
   * first part is what the guest is about to pay rather than what they have
   * paid, so it must not read as `paidToDate` — "Difference due today" keys off
   * that fact to tell a price change apart from a first charge.
   */
  hasCapturedPayments?: boolean;
  /**
   * Authoritative money movement for a pending guest offer/update. Contract
   * calculations describe pricing, while this settlement accounts for captured
   * and scheduled payments.
   */
  paymentSettlement?: GuestPaymentSettlement | null;
  /** Master `payment_option` — used for `payAtVenueSelected`. */
  paymentOption?: string | null;
};

/** Matches checkout `PAYMENT_OPTIONS.PAY_AT_VENUE_WITH_DEPOSIT`. */
const PAY_AT_VENUE_WITH_DEPOSIT = "PAY_AT_VENUE_WITH_DEPOSIT";

const isUsListingCountry = (country: string | null | undefined): boolean =>
  country === "US";

const getCleaningFeeAmount = (
  booking: WireBooking | null | undefined,
  calculations: WireContractCalculations | null | undefined,
): number => {
  // The original panel of a custom offer is read from a master with no
  // space-rental child, and the master repeats the charges on its own
  // calculations in the same shape.
  const charges = booking?.addtl_charges ?? calculations?.addtl_charges;
  const cleaningFee = charges?.find((charge) => charge.type === "CLEANING_FEE");

  return cleaningFee?.value ?? cleaningFee?.charge_amount ?? 0;
};

const getDayRateDiscount = (booking: WireBooking | null | undefined) => {
  const dayRateDiscounts =
    booking?.discounts?.filter(
      (discount) => discount.type === "DAY_RATE_DISCOUNT",
    ) ?? [];

  if (dayRateDiscounts.length === 0) {
    return { amount: null as number | null, percent: null as number | null };
  }

  const amount = dayRateDiscounts.reduce(
    (sum, discount) => sum + (discount.discount_amount ?? 0),
    0,
  );
  const percent =
    dayRateDiscounts.length === 1 ? (dayRateDiscounts[0]?.value ?? null) : null;

  return {
    amount: amount === 0 ? null : amount,
    percent,
  };
};

/**
 * Non-day-rate booking discounts → entitlement fact (BE provides label string).
 */
const getEntitlement = (
  booking: WireBooking | null | undefined,
): PriceFacts["entitlement"] => {
  const entitlements =
    booking?.discounts?.filter(
      (discount: WireContractDiscount & { title?: string }) =>
        discount.type !== "DAY_RATE_DISCOUNT" &&
        (discount.discount_amount ?? 0) !== 0,
    ) ?? [];

  if (entitlements.length === 0) return null;

  const amount = entitlements.reduce(
    (sum, discount) => sum + (discount.discount_amount ?? 0),
    0,
  );
  const first = entitlements[0] as WireContractDiscount & { title?: string };
  const label = first.title ?? first.discount_str;
  // Only caption when `title` took the label slot, so `discount_str` isn't
  // shown twice (once as label, once as caption) when there's no title.
  const caption = first.title != null ? first.discount_str : undefined;

  if (!label || amount === 0) return null;

  return { label, amount, caption };
};

const getPositiveFee = (
  schedule: { fee?: number | null } | null | undefined,
): number | null =>
  schedule?.fee != null && schedule.fee > 0 ? schedule.fee : null;

/**
 * Normalizes contract/quote calculations + listing context into flat `PriceFacts`.
 * Wire quirks (snake_case paths, DIY vs FSV space-rental math) live here.
 */
const mapCalculationsToPriceFacts = ({
  bookingStart: bookingStartParam = null,
  calculations,
  credits = 0,
  disableSalesTax = false,
  durationHours = null,
  eventEnd = null,
  hasCapturedPayments = false,
  hasPaymentSchedule = false,
  listing,
  paymentOption = null,
  paymentSettlement = null,
  spaceRentalBooking = null,
  upcomingChargeDate: upcomingChargeDateParam = null,
}: MapCalculationsToPriceFactsParams): PriceFacts => {
  const isDiy = listing?.full_service?.active !== true;
  const dayRateDiscount = getDayRateDiscount(spaceRentalBooking);
  const cleaningFeeAmount = getCleaningFeeAmount(spaceRentalBooking, calculations);
  const entitlement = getEntitlement(spaceRentalBooking);
  const payAtVenue = calculations?.guest?.pay_at_venue as
    | WirePayAtVenue
    | undefined;
  const paymentSchedule = calculations?.guest?.payment_schedule;
  const splitPayment = calculations?.guest?.split_payment;
  const updateDifference = calculations?.update_difference;

  let spaceRental: number | null = null;
  if (calculations != null || spaceRentalBooking != null) {
    if (isDiy) {
      // Keep discounts and cleaning as separate facts (not folded into space rental).
      spaceRental =
        spaceRentalBooking?.subtotal_amount ??
        calculations?.space_rental_guest ??
        0;
    } else {
      spaceRental = calculations?.space_rental_guest ?? 0;
    }
  }

  const foodDrinkMinimum =
    calculations?.included_items_guest ??
    calculations?.included_items?.amount ??
    null;

  const addOnsTotal =
    calculations?.add_ons_guest != null ? calculations.add_ons_guest : null;

  const processingFee =
    calculations?.guest?.platform_fee_amount ??
    calculations?.guest?.fee ??
    null;
  const originalProcessingFee =
    calculations?.guest?.original_fees?.fee != null
      ? calculations.guest.original_fees.fee
      : null;

  const taxes = disableSalesTax
    ? null
    : calculations?.guest?.total_tax_amount != null
      ? calculations.guest.total_tax_amount
      : null;

  const splitPaymentFee = hasPaymentSchedule
    ? (getPositiveFee(paymentSchedule) ?? getPositiveFee(splitPayment))
    : null;

  const hasPayAtVenueCalculations = payAtVenue != null;
  const payAtVenueSelected = paymentOption === PAY_AT_VENUE_WITH_DEPOSIT;
  const bookingTotal = hasPayAtVenueCalculations
    ? (payAtVenue.platform_and_venue_total ??
      payAtVenue.full_guest_payment_amount ??
      calculations?.guest?.payment ??
      null)
    : (calculations?.guest?.payment ?? null);

  const scheduleParts = hasPaymentSchedule
    ? (paymentSchedule?.parts ?? splitPayment?.parts)
    : undefined;
  const scheduleFirstPart =
    Array.isArray(scheduleParts) && typeof scheduleParts[0] === "number"
      ? scheduleParts[0]
      : null;
  const scheduleSecondPart =
    Array.isArray(scheduleParts) && typeof scheduleParts[1] === "number"
      ? scheduleParts[1]
      : null;

  let dueToday: number | null = null;
  if (paymentSettlement != null) {
    const usesDueTodayLabel =
      paymentSettlement.isOfferFromInquiry === true ||
      paymentSettlement.currentPaidTotal <= 0;
    dueToday =
      usesDueTodayLabel && paymentSettlement.dueNow > 0
        ? paymentSettlement.dueNow
        : null;
  } else if (hasPayAtVenueCalculations) {
    dueToday =
      payAtVenue.deposit_amount ?? payAtVenue.guest_payment_amount ?? null;
  } else if (scheduleFirstPart != null) {
    // `parts` partition the guest total, which already carries the schedule fee
    // as its own Section 1 line, so the fee must not be added on top again.
    dueToday = scheduleFirstPart;
  }

  const dueAtVenue = hasPayAtVenueCalculations
    ? (payAtVenue.due_at_venue_amount ?? null)
    : null;

  const guestPaymentAmount = hasPayAtVenueCalculations
    ? payAtVenueSelected
      ? (payAtVenue.guest_payment_amount ?? null)
      : (payAtVenue.full_guest_payment_amount ?? null)
    : null;

  const upcomingCharge = (() => {
    if (!hasPaymentSchedule) return null;
    if (paymentSettlement != null) {
      return paymentSettlement.scheduledPayment != null &&
        paymentSettlement.scheduledPayment > 0
        ? paymentSettlement.scheduledPayment
        : null;
    }
    if (scheduleSecondPart != null && scheduleSecondPart > 0) {
      return scheduleSecondPart;
    }
    if (
      updateDifference?.scheduled_payment != null &&
      updateDifference.scheduled_payment > 0
    ) {
      return updateDifference.scheduled_payment;
    }
    return null;
  })();

  const upcomingChargeDate =
    upcomingCharge != null
      ? (paymentSettlement?.scheduledChargeDate ??
        upcomingChargeDateParam ??
        updateDifference?.scheduled_charge_date ??
        null)
      : null;

  const priceDifference = (() => {
    if (paymentSettlement != null) {
      if (paymentSettlement.refund != null && paymentSettlement.refund > 0) {
        return -paymentSettlement.refund;
      }
      if (
        paymentSettlement.currentPaidTotal > 0 &&
        paymentSettlement.dueNow > 0 &&
        paymentSettlement.isOfferFromInquiry !== true
      ) {
        return paymentSettlement.dueNow;
      }
      return null;
    }
    if (!updateDifference) return null;
    if (updateDifference.due_now != null && updateDifference.due_now !== 0) {
      return updateDifference.due_now;
    }
    if (updateDifference.refund != null && updateDifference.refund > 0) {
      return -updateDifference.refund;
    }
    return null;
  })();

  const additionalPaymentsTotal =
    updateDifference?.additional_payment != null &&
    updateDifference.additional_payment > 0
      ? updateDifference.additional_payment
      : null;

  const hostFee =
    calculations?.host?.fee != null ? calculations.host.fee : null;
  const hostPlatformFeeAmount =
    calculations?.host?.platform_fee_amount ?? hostFee;
  const hostVatAmount =
    calculations?.host?.vat_amount != null && calculations.host.vat_amount > 0
      ? calculations.host.vat_amount
      : null;
  const payout =
    calculations?.host?.payout != null ? calculations.host.payout : null;
  // The amount the host fee is taken on; `subtotal_host` is the older field.
  const earningsSubtotal =
    calculations?.take_basis_amount_host ?? calculations?.subtotal_host ?? null;
  // Pay-at-venue quotes report `host.payout` as 0 because the guest pays the
  // venue directly, so earnings have to come off the fee basis instead.
  const estimatedEarnings =
    earningsSubtotal != null ? earningsSubtotal - (hostFee ?? 0) : payout;

  const hasVat =
    calculations?.guest?.vat_amount != null ||
    calculations?.host?.vat_amount != null;

  const hourlyRate =
    spaceRentalBooking?.rate ?? spaceRentalBooking?.rate_details?.rate ?? null;

  const gratuityAmount =
    calculations?.gratuity_amount != null && calculations.gratuity_amount > 0
      ? calculations.gratuity_amount
      : null;
  const gratuityRate =
    calculations?.gratuity_rate != null && calculations.gratuity_rate > 0
      ? calculations.gratuity_rate
      : null;

  const collectedAtVenueAddOns =
    calculations?.add_ons?.amount != null && calculations.add_ons.amount > 0
      ? calculations.add_ons.amount
      : null;

  const listingCountry = listing?.country ?? null;
  const isDepositListing =
    listing?.pay_at_venue?.type === "minimum_spend_deposit";

  return createEmptyPriceFacts({
    isDiy,
    hasVat,
    datesKnown: true,
    hourlyRate,
    spaceRental,
    dayRateDiscount: dayRateDiscount.amount,
    dayRateDiscountPercent: dayRateDiscount.percent,
    entitlement,
    foodDrinkMinimum,
    addOnsTotal,
    additionalPaymentsTotal,
    cleaningFee: cleaningFeeAmount > 0 ? cleaningFeeAmount : null,
    splitPaymentFee,
    processingFee,
    originalProcessingFee,
    taxes,
    credits,
    bookingTotal,
    earningsSubtotal,
    hostFee,
    estimatedEarnings,
    payout,
    dueToday,
    dueAtVenue,
    paidToDate:
      paymentSettlement?.currentPaidTotal ??
      (hasCapturedPayments
        ? hasPaymentSchedule
          ? scheduleFirstPart
          : (calculations?.guest?.payment ?? null)
        : null),
    priceDifference,
    upcomingCharge,
    upcomingChargeDate:
      upcomingChargeDate != null ? String(upcomingChargeDate) : null,
    durationHours: durationHours ?? spaceRentalBooking?.duration ?? null,
    roomType: listing?.full_service?.room_type ?? null,
    foodDrinkType: getFoodDrinkType({
      drinkOption: listing?.full_service?.drink_options,
      foodPackage: listing?.full_service?.food_packages?.[0],
    }),
    eventEnd,
    bookingStart:
      bookingStartParam ??
      (spaceRentalBooking?.dts_start
        ? String(spaceRentalBooking.dts_start)
        : null),
    gratuityAmount,
    gratuityRate,
    hostPlatformFeeAmount:
      hostPlatformFeeAmount != null && hostPlatformFeeAmount !== 0
        ? hostPlatformFeeAmount
        : null,
    hostVatAmount,
    guestPaymentAmount,
    collectedAtVenueAddOns,
    hasPayAtVenueCalculations,
    payAtVenueSelected,
    isDepositListing,
    listingCountry,
    isUsListing: isUsListingCountry(listingCountry),
  });
};

export type { GuestPaymentSettlement };
export { mapCalculationsToPriceFacts };
